import React, { createContext, useContext, useState, useCallback, useEffect } from 'react'

export type ToastType = 'success' | 'error' | 'info'

export interface ToastAction {
  label: string
  onClick(): void
}

export interface ToastOptions {
  duration?: number
  action?: ToastAction
}

interface Toast {
  id: string
  message: string
  type: ToastType
  duration: number
  action?: ToastAction
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType, options?: ToastOptions) => void
}

const ToastContext = createContext<ToastContextValue>({
  showToast: () => {},
})

export function useToast() {
  return useContext(ToastContext)
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const showToast = useCallback((message: string, type: ToastType = 'info', options?: ToastOptions) => {
    const id = Math.random().toString(36).slice(2)
    const duration = options?.duration ?? 3000
    setToasts((prev) => [...prev, { id, message, type, duration, action: options?.action }])

    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, duration)
  }, [])

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={(id) => setToasts((p) => p.filter((t) => t.id !== id))} />
    </ToastContext.Provider>
  )
}

function ToastContainer({
  toasts,
  onDismiss,
}: {
  toasts: Toast[]
  onDismiss: (id: string) => void
}) {
  if (toasts.length === 0) return null

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  )
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true))
  }, [])

  const bgColors: Record<ToastType, string> = {
    success: '#2D6A4F',
    error: '#C0392B',
    info: 'var(--color-ink)',
  }

  return (
    <div
      onClick={() => onDismiss(toast.id)}
      style={{
        background: bgColors[toast.type],
        color: 'var(--color-paper)',
        padding: '10px 16px',
        borderRadius: 'var(--radius-md)',
        fontFamily: 'var(--font-serif)',
        fontSize: '0.875rem',
        cursor: 'pointer',
        maxWidth: '320px',
        transform: visible ? 'translateX(0)' : 'translateX(20px)',
        opacity: visible ? 1 : 0,
        transition: 'transform 0.2s ease, opacity 0.2s ease',
        boxShadow: 'var(--shadow-md)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}
    >
      <span style={{ flex: 1 }}>{toast.message}</span>
      {toast.action && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            toast.action!.onClick()
            onDismiss(toast.id)
          }}
          style={{
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.5)',
            color: 'var(--color-paper)',
            cursor: 'pointer',
            padding: '2px 8px',
            borderRadius: 'var(--radius-sm)',
            fontFamily: 'var(--font-serif)',
            fontSize: '0.8rem',
            flexShrink: 0,
          }}
        >
          {toast.action.label}
        </button>
      )}
    </div>
  )
}
